import clsx from 'clsx';
import type { BuildState } from '@/types';
import { recommendSpine, suggestSpineForState } from '@/lib/spine';
import { getInsertWeight } from '@/lib/calculations';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SpineWhatIfPanel } from './SpineWhatIfPanel';

interface SpineCheckProps {
  state: BuildState;
  setField: <K extends keyof BuildState>(field: K, value: BuildState[K]) => void;
}

const statusStyle = {
  ok:    { ring: 'ring-emerald-500/40', bg: 'bg-emerald-950/40', text: 'text-emerald-400', label: 'IN RANGE' },
  stiff: { ring: 'ring-amber-500/40',   bg: 'bg-amber-950/40',   text: 'text-amber-400',   label: 'TOO STIFF' },
  weak:  { ring: 'ring-rose-500/40',    bg: 'bg-rose-950/40',    text: 'text-rose-400',    label: 'TOO WEAK' },
} as const;

export function SpineCheck({ state, setField }: SpineCheckProps) {
  const rec = recommendSpine(state);
  const styles = statusStyle[rec.status];
  const suggested = suggestSpineForState(state);

  return (
    <Card padding="lg" className={clsx('ring-1', styles.ring)}>
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-semibold">Spine Suitability</h2>
        <span className={clsx('text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded', styles.bg, styles.text)}>
          {styles.label}
        </span>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <Stat label="Selected" value={`${state.spineKey}`} subtle={false} />
        <Stat label="Recommended" value={`${rec.recommendedMin}–${rec.recommendedMax}`} subtle={false} accent />
        <Stat
          label="Effective DW"
          value={`${rec.effectiveDrawWeight.toFixed(0)} lb`}
          subtle
        />
      </div>

      <p className="text-sm text-zinc-300 leading-snug">{rec.message}</p>

      {rec.status !== 'ok' && suggested !== null && suggested !== state.spineKey && (
        <div className="mt-3">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setField('spineKey', suggested)}
          >
            Switch to {suggested} spine
          </Button>
        </div>
      )}

      <p className="text-[11px] text-zinc-500 mt-3">
        Effective draw weight = peak DW + ((arrow length − 28") × 3.5) + ((point − 100 gr) × 0.12)
        + ((insert over 25 gr) × 0.12). Coefficients validated against Easton + Victory charts.
      </p>

      {/* v137 — Spine What-If panel. Renders only when the user's selected
          spine is out of window (stiff or weak). Diagnostic-only, no mutation. */}
      {rec.status !== 'ok' && (
        <SpineWhatIfPanel
          inputs={{
            drawWeight: state.bow.drawWeight,
            bowType: state.bow.type,
            iboSpeed: state.bow.iboSpeed,
            letOff: state.bow.letOff,
            arrowLengthIn: state.length,
            pointGr: state.pointWeight,
            insertGr: getInsertWeight(state),
          }}
          currentSpine={state.spineKey}
          variant="card"
        />
      )}
    </Card>
  );
}

function Stat({ label, value, subtle, accent }: { label: string; value: string; subtle?: boolean; accent?: boolean }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2">
      <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{label}</div>
      <div
        className={clsx(
          'font-mono text-sm tabular-nums',
          accent && 'text-ember-500 font-bold',
          subtle && 'text-zinc-400',
        )}
      >
        {value}
      </div>
    </div>
  );
}
